import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'

import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import Checkbox from 'components/Checkbox/checkbox'
import apiService from 'services/api'
import { TabKeys } from './SideMenu'
import { UserNotificationSettingSummary } from 'models/notifications_preferences'

import * as S from './styles'

export function NotificationPreferencesTable({ tab }: { tab: TabKeys }) {
  const api = apiService()
  const { data, isLoading } = api.useGetNotificationsPreferencesByKind(tab)
  const { mutate: mutateUpdateAllNotificationsPreferencesByKind } =
    api.useUpdateAllNotificationsPreferencesByKind()
  const { mutate: mutateUpdateNotificationsPreferences } =
    api.useUpdateNotificationsPreferences()

  const handleCheckAll = async (data: {
    emailEnabled?: boolean
    inAppEnabled?: boolean
  }) => {
    mutateUpdateAllNotificationsPreferencesByKind({
      kindSlug: tab,
      emailEnabled: data.emailEnabled,
      inAppEnabled: data.inAppEnabled,
    })
  }

  const handleCheckOne = async (
    preferenceSlug: string,
    data: {
      emailEnabled?: boolean
      inAppEnabled?: boolean
    }
  ) => {
    mutateUpdateNotificationsPreferences({
      preferenceSlug: preferenceSlug,
      kindSlug: tab,
      data,
    })
  }

  const columns = useMemo<
    ColumnDef<UserNotificationSettingSummary, any>[]
  >(() => {
    const currentColunms: ColumnDef<UserNotificationSettingSummary, any>[] = [
      {
        header: 'Notification',
        cell: ({ row }) => {
          return row.original?.preference_description
        },
      },
      {
        id: 'all-email',
        size: 40,
        header: () => {
          const allChecked = data
            ?.filter((row) => row.is_enabled_email)
            .every((row) => row.email_enabled)
          return (
            <div className="flex justify-center gap-2">
              <Checkbox
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({
                    emailEnabled: e.target.checked,
                  })
                }
              />
              <span>Email</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_email) {
            return (
              <Checkbox
                checked={row.original.email_enabled}
                onChange={(e) =>
                  handleCheckOne(row.original.preference_slug, {
                    emailEnabled: e.target.checked,
                  })
                }
              />
            )
          }

          return null
        },
      },
      {
        id: 'all-in-app',
        size: 40,
        meta: {
          isLastColumn: true,
        },
        header: () => {
          const allChecked = data
            ?.filter((row) => row.is_enabled_in_app)
            .every((row) => row.in_app_enabled)
          return (
            <div className="flex justify-center gap-2">
              <Checkbox
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({ inAppEnabled: e.target.checked })
                }
              />
              <span>In App</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_in_app) {
            return (
              <Checkbox
                checked={row.original.in_app_enabled}
                onChange={(e) =>
                  handleCheckOne(row.original.preference_slug, {
                    inAppEnabled: e.target.checked,
                  })
                }
              />
            )
          }

          return null
        },
      },
    ]

    return currentColunms
  }, [data])

  if (isLoading || !data) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <DataTableContainer>
      <ClientDataTable
        height={600}
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownTextTooltip
                tooltipText={`Select how you prefer to be notified about ${tab}.`}
                maxLines={4}
                placement="bottom"
              >
                Select how you prefer to be notified about{' '}
                <strong>{tab}</strong>.
              </OverflownTextTooltip>
            </S.TableDescriptionText>
          </S.SettingsTableHeader>
        }
        data={data || []}
        columns={columns}
      />
    </DataTableContainer>
  )
}
